const dialogSizes = ["small", "large"];

const defaultState = {
    dismissable: true,
    trigger: null,
    source: null,
    sourceType: null,
    url: null,
    size: "medium",
};

export function createDialog(dialog) {
    const content = dialog.querySelector(".dialog-scroll-content");
    const title = dialog.querySelector(".dialog-title");
    const closeButton = dialog.querySelector(".dialog-close");

    let state = { ...defaultState };
    let isClosingLocked = false;
    let localSourceElement = null;

    bindEvents();
    bindOpenTriggers();

    return {
        open,
        close,
        requestClose,
        toggle,
    };

    function bindEvents() {
        closeButton?.addEventListener("click", () => {
            requestClose("close-button");
        });

        dialog.addEventListener(
            "keydown",
            (event) => {
                if (event.key !== "Escape") return;
                if (state.dismissable) return;

                event.preventDefault();
                event.stopPropagation();
                event.stopImmediatePropagation();
            },
            true
        );

        dialog.addEventListener("beforetoggle", (event) => {
            if (event.newState === "open") {
                dispatch("dialog:beforeopen", { nativeEvent: event });
                return;
            }

            dispatch("dialog:beforeclose", {
                nativeEvent: event,
                reason: "toggle",
            });
        });

        dialog.addEventListener("toggle", (event) => {
            if (event.newState === "open") {
                isClosingLocked = false;
                dispatch("dialog:afteropen", { nativeEvent: event });
                return;
            }

            isClosingLocked = false;

            dispatch("dialog:afterclose", {
                nativeEvent: event,
                returnValue: dialog.returnValue,
            });

            emptyContent();
            resetPresentation();

            dialog.hidden = true;

            if (state.trigger instanceof HTMLElement) {
                state.trigger.focus();
            }

            state = { ...defaultState };
        });

        dialog.addEventListener("cancel", (event) => {
            dispatch("dialog:cancel", {
                nativeEvent: event,
                reason: "cancel",
            });

            if (!state.dismissable) {
                event.preventDefault();
                event.stopPropagation();
            }
        });

        dialog.addEventListener("click", (event) => {
            if (!state.dismissable) return;

            if (event.target === dialog) {
                requestClose("backdrop");
            }
        });

        dialog.addEventListener("submit", async (event) => {
            const form = event.target;
            if (!(form instanceof HTMLFormElement)) {
                return;
            }
            if (form.closest("dialog") !== dialog) {
                return;
            }
            const isValid = validateDialogForm(form);
            if (!isValid) {
                event.preventDefault();
                event.stopPropagation();
                event.stopImmediatePropagation();
                setFormSubmitButtonsDisabled(form, false);
                form.dispatchEvent(
                    new CustomEvent("sds:form:validated", {
                        bubbles: true,
                        detail: { valid: false },
                    })
                );
                return;
            }
            const isDialogTarget = form.dataset.dialogTarget === "true";
            const method = (form.getAttribute("method") || "get").trim().toLowerCase();
            if (!isDialogTarget || method !== "get") {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            setFormSubmitButtonsDisabled(form, true);
            const formData = new FormData(form);
            const params = new URLSearchParams(formData).toString();
            const action = form.getAttribute("action") || "";
            const url = params ? `${action}?${params}` : action;
            const elemId = form.dataset.elemId;
            const dialogOptions = {
                url,
                title: form.dataset.dialogTitle || title?.textContent || "",
                elemId: elemId || null,
                dismissable: state.dismissable,
                trigger: state.trigger,
                size: state.size,
            };
            await open(dialogOptions);
        }, true);
    }

    function bindOpenTriggers() {
        document.querySelectorAll("[data-elem-id]").forEach((button) => {
            if (button.dataset.dialogOpenBound === "true") {
                return;
            }
            button.addEventListener("click", function (event) {
                event.preventDefault();
                const url = this.dataset.url;
                const title = this.dataset.title;
                const elemId = this.dataset.elemId;
                window.dialog.open({
                    url: url,
                    title: title,
                    elemId: elemId
                });
            });
            button.dataset.dialogOpenBound = "true";
        });
    }

    async function open(options = {}) {
        const {
            title: nextTitle = "",
            url = null,
            elemId = null,
            dismissable = true,
            size = defaultState.size,
            trigger = document.activeElement instanceof HTMLElement
                ? document.activeElement
                : null,
        } = options;

        state = {
            dismissable,
            trigger,
            size: normalizeSize(size),
            source: elemId || null,
            url: url ? new URL(String(url), window.location.origin) : null,
            sourceType: url ? "url" : elemId ? "element" : null,
        };

        isClosingLocked = false;
        dialog.returnValue = "";

        if (title) {
            const textarea = document.createElement("textarea");
            textarea.innerHTML = nextTitle;
            title.textContent = textarea.value;
        }

        emptyContent();

        if (closeButton) {
            closeButton.hidden = !dismissable;
        }

        applySize(state.size);

        dialog.hidden = false;

        if (!dialog.open) {
            dialog.showModal();
        }

        if (url) {
            await loadRemoteContent(elemId);
        } else if (elemId) {
            loadLocalContent(elemId);
        }

        dialog.focus();

        if (content) {
            dispatch("dialog:contentready", { content });
        }
    }

    function close(returnValue = "") {
        isClosingLocked = false;

        if (!dialog.open) {
            dialog.hidden = true;
            resetPresentation();
            state = { ...defaultState };
            return;
        }

        dialog.close(returnValue);
    }

    function requestClose(reason = "api") {
        dispatch("dialog:requestclose", { reason });

        if (!state.dismissable) return;
        if (isClosingLocked) return;

        isClosingLocked = true;

        queueMicrotask(() => {
            isClosingLocked = false;
        });

        if ("requestClose" in dialog) {
            dialog.requestClose(reason);
            return;
        }

        dialog.close(reason);
    }

    async function toggle(options = {}) {
        if (dialog.open) {
            requestClose("toggle");
            return;
        }

        await open(options);
    }

    async function loadRemoteContent(elemId) {
        dialog.classList.add("loading");

        let response;

        try {
            response = await fetch(state.url, {
                credentials: "same-origin",
            });
        } catch (error) {
            // Fallback in case of an inactive state
            window.location.assign(window.location.href);
            return;
        }

        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, "text/html");

        dialog.classList.remove("loading");
        dialog.classList.add("loaded");

        if (elemId) {
            const remoteElement = parsed.getElementById(elemId);

            if (!remoteElement) {
                dialog.classList.remove("loaded");
                throw new Error(`Element with id "${elemId}" was not found in fetched markup.`);
            }
            
            if (content) {
                content.innerHTML = remoteElement.innerHTML;
            }
        } else if (content) {
            content.innerHTML = parsed.body ? parsed.body.innerHTML : html;
        }

        dialog.classList.remove("loaded");
    }

    function loadLocalContent(elemId) {
        const el = document.getElementById(elemId);

        if (!content || !el) return;

        localSourceElement = el;

        while (el.firstChild) {
            content.appendChild(el.firstChild);
        }
    }

    function emptyContent() {
        if (!content) return;

        dispatch("dialog:beforeempty", { content });

        if (localSourceElement) {
            while (content.firstChild) {
                localSourceElement.appendChild(content.firstChild);
            }

            localSourceElement = null;
        } else {
            content.innerHTML = "";
        }

        dispatch("dialog:afterempty", { content });
    }

    function applySize(size) {
        dialog.classList.remove("small", "large");
        dialog.classList.add(`${size}`);
    }

    function resetPresentation() {
        dialog.classList.remove("loading");
        dialog.classList.remove("loaded");
        dialog.classList.remove("small", "large");
    }

    function normalizeSize(size) {
        return dialogSizes.includes(size)
            ? size
            : defaultState.size;
    }

    function dispatch(type, detail = {}) {
        dialog.dispatchEvent(
            new CustomEvent(type, {
                bubbles: true,
                detail: {
                    dismissable: state.dismissable,
                    source: state.source,
                    url: state.url,
                    sourceType: state.sourceType,
                    trigger: state.trigger,
                    size: state.size,
                    returnValue: dialog.returnValue,
                    ...detail,
                },
            })
        );
    }
}

function validateDialogForm(form) {
    const $ = window.jQuery;
    if (!$ || !$.validator || !$.validator.unobtrusive) {
        return true;
    }
    const $form = $(form);
    $form
        .removeData("validator")
        .removeData("unobtrusiveValidation");
    $.validator.unobtrusive.parse(form);
    return $form.valid();
}
function setFormSubmitButtonsDisabled(form, disabled) {
    form.querySelectorAll('button[type="submit"], input[type="submit"], sds-button[type="submit"]').forEach((el) => {
        el.setAttribute("aria-disabled", disabled ? "true" : "false");
        if (el.tagName !== "SDS-BUTTON") {
            el.disabled = disabled;
        }
    });
}